import { Injectable } from '@angular/core';
import { IRetryPolicy, RetryContext } from '@microsoft/signalr';

const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS = 60_000;
const JITTER_MS = 5000;
const SHUTDOWN_HINT_VALIDITY_MS = 30_000;

/**
 * Custom retry policy used both by withAutomaticReconnect (transport-loss reconnects) and by
 * SignalRHubBase's initial-start retry loop (negotiate-503 during server drain).
 *
 * Differences from the @microsoft/signalr default:
 *  - Never returns null — clients keep trying forever, capped at MAX_DELAY_MS + jitter.
 *    The default policy gives up after 4 attempts, which strands long-lived UIs after a hiccup.
 *  - Adds per-call random jitter so N tabs don't synchronize their reconnect attempts and
 *    stampede the server on bring-up.
 *  - Honors a one-shot ServerShutdown hint from the server: when the backend broadcasts
 *    ServerShutdown with a retryAfterMs, the next retry randomizes within that bound instead
 *    of using the regular backoff. Hint is consumed after one use so subsequent retries
 *    fall back to exponential backoff.
 *
 * Registered providedIn:'root' so all hub services share the same hint state — when the
 * backend drains, the hint applies to every connected hub on the next reconnect.
 */
@Injectable({ providedIn: 'root' })
export class SignalRRetryPolicy implements IRetryPolicy {
	private _shutdownHint: { retryAfterMs: number; capturedAt: number } | null = null;

	captureShutdownHint(retryAfterMs: number): void {
		this._shutdownHint = { retryAfterMs, capturedAt: Date.now() };
	}

	nextRetryDelayInMilliseconds(retryContext: RetryContext): number | null {
		/** if a recent ServerShutdown hint exists, randomize within the bound the server sent and consume the hint */
		if (this._shutdownHint && Date.now() - this._shutdownHint.capturedAt < SHUTDOWN_HINT_VALIDITY_MS) {
			const hint = this._shutdownHint.retryAfterMs;
			this._shutdownHint = null;
			return Math.random() * hint;
		}

		/** exponential backoff capped at 60s, plus per-call jitter so clients spread out */
		const exp = Math.min(MAX_DELAY_MS, BASE_DELAY_MS * Math.pow(2, retryContext.previousRetryCount));
		return exp + Math.random() * JITTER_MS;
	}
}
